import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { useEditMode } from '../../shared/hooks/useEditMode';
import { useDeviceStore } from '../../shared/store/deviceStore';
import { useBillboardStore } from '../../shared/store/billboardStore';
import { useSetting } from '../../shared/hooks/useApi/useSetting';
import { useBillboard } from '../../shared/hooks/useApi/useBillboard';
import { useImage } from '../../shared/hooks/useApi/useImage';
import { usePageOwnerIdentityStore } from '../../shared/store/pageOwnerIdentityStore';
import { useImageStyleJudge } from '../../shared/hooks/useImageStyleJudge';
import { useAuth } from '../../shared/hooks/useApi/useAuth';
import OptimizedMoleculeImg from '../../shared/component/molecule/OptimizedMoleculeImg';
import SelectBox from '../../shared/component/SelectBox';
import { slideInFromTop } from '../../shared/component/Animation';
import {
  AlertPosition,
  AlertType,
  DeviceType,
  DisplayAlignment,
  DisplaySize,
  ImageStyle,
  SelectType,
  ServiceType,
} from '../../shared/enum/EnumRepository';
import { StyledImgProps } from '../../shared/dto/StyleCompRepository';
import { UpdateBillboardReq } from '../../shared/dto/ReqDtoRepository';
import { convertS3UrlToCloudFrontUrl } from '../../shared/aws/awsHelper';
import { getArchiveEntryPath } from '../../shared/util/archiveAccess';
import { useStandardAlertStore } from '../../shared/store/portal/alertStore';
import { getAlertMessage } from '../../shared/constants/alertMessages';
import { useNavigate } from 'react-router-dom';
import {
  BILLBOARD_IMAGE_SIZE,
  getCompactLandscapeWidth,
  getCompactPortraitHeight,
  getDisplaySizeValue
} from '../../shared/util/displayImageSize';

import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import confirmIcon from '../../asset/icon/confirm2.png';
import confirmIcon2 from '../../asset/icon/confirm3.png';


const BillboardImageContent: React.FC = () => {
  const [isHover, setIsHover] = useState(false);
  const pageOwner = usePageOwnerIdentityStore(s => s.pageOwner);
  const { isEditMode } = useEditMode();
  const { deviceType } = useDeviceStore();
  const { updateBillboard } = useBillboard();
  const { uploadImage } = useImage();
  const {
    billboard,
    hasChanged,
    imageChanged,
    updateImage: handleImageChange,
    updateDisplaySize: handleDisplaySizeChange
  } = useBillboardStore();
  const { setting } = useSetting();
  const { isOwner } = useAuth();
  const { enqueueAlert } = useStandardAlertStore();
  const navigate = useNavigate();
  const { judgeImageStyleBillboard } = useImageStyleJudge();

  const isMobile = deviceType === DeviceType.MOBILE;
  const isPrivate = !setting?.menuVisible.billboard;
  const displaySize = billboard?.displaySize || DisplaySize.BIG;
  // 모바일에서는 SMALL·REGULAR·MEDIUM도 BIG으로 렌더. WIDE는 유지
  const visualDisplaySize =
    isMobile && displaySize !== DisplaySize.WIDE ? DisplaySize.BIG : displaySize;
  const originUrl = billboard?.uploadFile?.originUrl || '';
  const width = billboard?.uploadFile?.width || 0;
  const height = billboard?.uploadFile?.height || 0;
  const imageRatio = width > 0 && height > 0 ? width / height : undefined;
  const showUsername = !isEditMode && (isMobile || isHover);
  

  const handleOnClick = () => {
    if (!billboard || !pageOwner || !setting || isEditMode) return;
    if (isOwner) {
      navigate(`/@${pageOwner.handle}/projects`);
      return;
    }

    const entryPath = getArchiveEntryPath(setting, pageOwner.handle, false);
    if (entryPath) {
      navigate(entryPath);
      return;
    }

    enqueueAlert({
      type: AlertType.ALERT,
      position: AlertPosition.TOP,
      content: getAlertMessage('ALL_CONTENT_PRIVATE'),
    });
  };

  const convertedCloudFrontUrl = useMemo(() => {
    return convertS3UrlToCloudFrontUrl(originUrl);
  }, [originUrl]);

  const styledImageComponent = useMemo(() => {
    const imageStyle = judgeImageStyleBillboard(width / height);
    if (imageStyle === ImageStyle.PORTRAIT) {
      return StyledImagePortrait as React.ComponentType<StyledImgProps>;
    } else {
      return StyledImageLandscape as React.ComponentType<StyledImgProps>;
    }
  }, [width, height]);

  if (!billboard) {
    return null;
  }


  const handleUpdate = async () => {
    if (!billboard) return;
    
    const { uploadFile, ...billboardWithoutUploadFile } = billboard;
    const baseRequest: UpdateBillboardReq = {
      ...billboardWithoutUploadFile,
      updateUploadFileReq: {
        originUrl: uploadFile.originUrl,
        uploadFileId: uploadFile.id,
        allowDownload: uploadFile.allowDownload,
        width: uploadFile.width,
        height: uploadFile.height,
      },
    };

    if (!imageChanged) {
      await updateBillboard(baseRequest);
      return;
    }

    const finalRequest = await uploadImage(ServiceType.BILLBOARD, baseRequest);
    if (!finalRequest) return;

    await updateBillboard(finalRequest as UpdateBillboardReq);
  };

  return (
    <ImgInPeWrapper
      $isPrivate={isPrivate}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
    >
      {isEditMode && (
        <PEHeader>
          <SelectBoxContainer>
            <SelectBoxWrapper>
              <SelectBox
                value={displaySize}
                selectType={SelectType.WORK_SIZE}
                handleChange={handleDisplaySizeChange}
                direction={false}
              />
            </SelectBoxWrapper>
          </SelectBoxContainer>
          <PEIndicator>: Billboard</PEIndicator>
        </PEHeader>
      )}
      <OptimizedMoleculeImg
        srcUrl={convertedCloudFrontUrl}
        alt="Billboard"
        displaySize={visualDisplaySize}
        displayAlignment={DisplayAlignment.CENTER}
        handleChange={handleImageChange}
        StyledImg={styledImageComponent}
        priority={true}
        handleOnClick={handleOnClick}
        imageRatio={imageRatio}
      />
      {showUsername && <Username>{setting?.username}</Username>}

      {isEditMode && hasChanged && (
        <BtnContainer>
          <UniversalIconBtn
            isPrimary={true}
            size="fit"
            border={false}
            backgroundImg={confirmIcon}
            backgroundImgHover={confirmIcon2}
            onClick={handleUpdate}
          />
        </BtnContainer>
      )}
    </ImgInPeWrapper>
  );
};

const ImgInPeWrapper = styled.div<{ $isPrivate: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-grow: 0;
  opacity: ${({ $isPrivate }) => ($isPrivate ? 0.5 : 1)};
`;

const PEHeader = styled.div`
  position: absolute;
  top: -25px;
  width: 100%;
  display: flex;
  justify-content: space-between;
`;

const PEIndicator = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  color: ${({ theme }) => theme.colors.color_Gray_05};
  ${({ theme }) => theme.typography.Body_03_1};
`;

const SelectBoxContainer = styled.div`
  width: 100%;
  display: flex;
  gap: clamp(12px, 1vw, 14px);
`;

const SelectBoxWrapper = styled.article`
  width: clamp(70px, 16vw, 100px);
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_04};
`;

const Username = styled.div`
  position: absolute;
  bottom: -18px;
  left: 0;
  width: 100%;
  display: flex;
  justify-content: flex-start;
  ${({ theme }) => theme.typography.Body_03_1};
  ${slideInFromTop}
`;

const StyledImagePortrait = styled.img<{ $displaySize: DisplaySize; $imageRatio?: number; $isEditMode?: boolean; }>`
  height: ${({ $displaySize, $imageRatio }) =>
    `min(80vh, ${getCompactPortraitHeight($displaySize, $imageRatio, BILLBOARD_IMAGE_SIZE)})`};
  width: auto;
  max-height: 80vh;
  transition: height 0.6s ease, max-width 0.6s ease;
  object-fit: contain;
  max-width: ${({ $displaySize }) =>
    `${getDisplaySizeValue($displaySize, BILLBOARD_IMAGE_SIZE.widthVw)}vw`};
  cursor: ${({ $isEditMode }) => ($isEditMode ? 'default' : 'pointer')};
`;

const StyledImageLandscape = styled.img<{ $displaySize: DisplaySize; $imageRatio?: number; $isEditMode?: boolean; }>`
  width: ${({ $displaySize, $imageRatio }) =>
    getCompactLandscapeWidth(
      $displaySize,
      $imageRatio,
      {
        widthVw: BILLBOARD_IMAGE_SIZE.heightVh,
        heightVh: {
          ...BILLBOARD_IMAGE_SIZE.widthVw,
          [DisplaySize.BIG]: 80,
          [DisplaySize.WIDE]: 80,
        },
      }
    )};
  height: auto;
  max-height: 80vh;
  transition: width 0.6s ease, max-height 0.6s ease;
  object-fit: contain;
  cursor: ${({ $isEditMode }) => ($isEditMode ? 'default' : 'pointer')};
`;


const BtnContainer = styled.div`
  position: absolute;
  right: 0.5vh;
  top: 0.5vh;
  width: fit-content;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.5vw;
`;


export default BillboardImageContent;
